import Backbone from "backbone";
import $ from "jquery";
import _ from "underscore";
import config from "../config";
import Constants from "./Constants";
import Cookies from "./Cookies";

// import "https://dev-widget.simulationcurriculum.com/skysafari.js";

let cssLoaded = false;
const PERSISTENT_CONTAINER_IDS = new Set([
  "skysafariObjectContainer",
  "skysafariObjectContainerPersistent",
]);
const WIDGET_PARKING_ID = "skysafariWidgetParking";
const SKYCAST_WIDGET_CONTAINER_PATTERN = /^skyCastGuestSkySafariContainer-/;
const SKYSAFARI_ZOOM_REPEAT_MS = 80;
const SKYSAFARI_ZOOM_REPEAT_DELAY_MS = 260;
const SKYSAFARI_ZOOM_IN_FACTOR = 0.72;
const SKYSAFARI_ZOOM_OUT_FACTOR = 1.38;
const MIN_SKYSAFARI_FOV_DEGREES = 0.05;
const _lastAssociateParams = {};
const _contextLossRecoveryGuard = {};
const _contextLossRecoveryCooldownMs = 5000;
const _contextWasLostFlags = {};

const ensureWidgetParkingContainer = () => {
  if (typeof document === "undefined") {
    return null;
  }

  let parking = document.getElementById(WIDGET_PARKING_ID);
  if (parking) {
    return parking;
  }

  parking = document.createElement("div");
  parking.id = WIDGET_PARKING_ID;
  parking.setAttribute("aria-hidden", "true");
  parking.style.position = "fixed";
  parking.style.left = "-10000px";
  parking.style.top = "-10000px";
  parking.style.width = "1px";
  parking.style.height = "1px";
  parking.style.overflow = "hidden";
  parking.style.pointerEvents = "none";
  document.body.appendChild(parking);
  return parking;
};

// Intercept native addEventListener to capture scroll handlers added by the
// widget's bundled jQuery (which is separate from our page jQuery).
const _startScrollTrap = () => {
  if (typeof window === "undefined") return { stop() {}, captured: [] };
  const captured = [];
  const origAdd = window.addEventListener;
  window.addEventListener = function (type, fn, opts) {
    if (type === "scroll") {
      captured.push({ fn, opts });
    }
    return origAdd.call(this, type, fn, opts);
  };
  return {
    captured,
    stop() {
      window.addEventListener = origAdd;
    },
  };
};

// Remove all scroll handlers captured by a trap.
const _removeCapturedScrollHandlers = (trap) => {
  trap.stop();
  trap.captured.forEach(({ fn, opts }) => {
    window.removeEventListener("scroll", fn, opts);
  });
};

// Attach a webglcontextlost listener to the widget's canvas.
// This fires at the moment of context loss (during sleep), before any
// auto-restoration attempt, so the flag is set even if Chrome later
// restores the raw GL context.
const _attachContextLossListener = (containerId, ssww) => {
  const canvas = ssww.element?.[0]?.querySelector("canvas");
  if (!canvas) return;

  const handler = (e) => {
    _contextWasLostFlags[containerId] = true;
    console.warn(`SkySafari widget "${containerId}" webglcontextlost event`);
  };
  canvas.addEventListener("webglcontextlost", handler);
  ssww._contextLostHandler = handler;
  ssww._contextLostCanvas = canvas;
};

// Check if a persisted widget's WebGL context is still valid.
const _isWidgetHealthy = (containerId) => {
  const initState = Portal.Current?.skySafariObjectInitStates?.[containerId];
  if (initState !== "ready") return true; // still initializing, don't interfere

  if (_contextWasLostFlags[containerId]) return false;

  const ssww = Portal.Current?.skySafariObjects?.[containerId];
  if (!ssww?.element?.length) return false;

  const canvas = ssww.element[0]?.querySelector("canvas");
  if (!canvas) return true; // no canvas yet, can't determine

  const gl = canvas.getContext("webgl2") || canvas.getContext("webgl");
  if (!gl) return true; // can't determine

  return !gl.isContextLost();
};

const _shouldRecoverContextLoss = (containerId) => {
  const now = Date.now();
  const lastRecovery = _contextLossRecoveryGuard[containerId] || 0;
  if (now - lastRecovery < _contextLossRecoveryCooldownMs) {
    return false;
  }

  _contextLossRecoveryGuard[containerId] = now;
  return true;
};

const _setSkySetValue = (skySetFileData, key, value) => {
  const nextLine = `${key}=${value}`;
  const settingPattern = new RegExp(`^${key}=.*$`, "gm");

  if (settingPattern.test(skySetFileData)) {
    return skySetFileData.replace(settingPattern, nextLine);
  }

  return `${skySetFileData.trimEnd()}\n${nextLine}\n`;
};

const _getSkySetNumberValue = (skySetFileData, key) => {
  const settingPattern = new RegExp(`^${key}=([^\\n\\r]*)`, "m");
  const value = settingPattern.exec(skySetFileData)?.[1];
  const parsedValue = value ? Number(value) : Number.NaN;

  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const _zoomSkySafariWidgetSettings = (ssww, zoomFactor) => {
  if (!ssww?.settings?.fetch || !ssww?.settings?.load) {
    return Promise.resolve();
  }

  const currentSettings = ssww.settings.fetch();
  const currentFovDegrees = _getSkySetNumberValue(
    currentSettings,
    "DisplayFOV",
  );
  if (!currentFovDegrees) return Promise.resolve();

  const nextFovDegrees = Math.max(
    MIN_SKYSAFARI_FOV_DEGREES,
    currentFovDegrees * zoomFactor,
  );
  const nextSettings = _setSkySetValue(
    currentSettings,
    "DisplayFOV",
    nextFovDegrees.toPrecision(16),
  );

  return ssww.settings.load(nextSettings, 0, 0);
};

const _attachSkyCastUnrestrictedZoomControls = (
  containerId,
  ssww,
  attempt = 0,
) => {
  if (
    !SKYCAST_WIDGET_CONTAINER_PATTERN.test(containerId) ||
    !ssww?.element?.[0]
  ) {
    return;
  }

  const bindButton = (selector, zoomFactor) => {
    const button = ssww.element[0].querySelector(selector);
    if (!button) return false;
    if (button._portalSkyCastUnrestrictedZoomBound) return true;

    let repeatDelay = null;
    let repeatInterval = null;
    const clearRepeat = () => {
      clearTimeout(repeatDelay);
      clearInterval(repeatInterval);
      repeatDelay = null;
      repeatInterval = null;
      document.removeEventListener("mouseup", clearRepeat, true);
      document.removeEventListener("touchend", clearRepeat, true);
      document.removeEventListener("touchcancel", clearRepeat, true);
    };
    const zoom = () => {
      Promise.resolve(_zoomSkySafariWidgetSettings(ssww, zoomFactor)).catch(
        (error) => console.warn("SkySafari unrestricted zoom failed", error),
      );
    };
    const startZoom = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      clearRepeat();
      zoom();
      repeatDelay = setTimeout(() => {
        repeatInterval = setInterval(zoom, SKYSAFARI_ZOOM_REPEAT_MS);
      }, SKYSAFARI_ZOOM_REPEAT_DELAY_MS);
      document.addEventListener("mouseup", clearRepeat, true);
      document.addEventListener("touchend", clearRepeat, true);
      document.addEventListener("touchcancel", clearRepeat, true);
    };
    const stopNativeClick = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    const cleanup = () => {
      clearRepeat();
      button.removeEventListener("mousedown", startZoom, true);
      button.removeEventListener("touchstart", startZoom, true);
      button.removeEventListener("click", stopNativeClick, true);
      delete button._portalSkyCastUnrestrictedZoomBound;
      delete button._portalSkyCastUnrestrictedZoomCleanup;
    };

    button.addEventListener("mousedown", startZoom, true);
    button.addEventListener("touchstart", startZoom, true);
    button.addEventListener("click", stopNativeClick, true);
    button._portalSkyCastUnrestrictedZoomBound = true;
    button._portalSkyCastUnrestrictedZoomCleanup = cleanup;

    return true;
  };

  const hasZoomIn = bindButton("#zoomInButton", SKYSAFARI_ZOOM_IN_FACTOR);
  const hasZoomOut = bindButton("#zoomOutButton", SKYSAFARI_ZOOM_OUT_FACTOR);
  const rootElement = ssww.element[0];

  if (!rootElement._portalSkyCastUnrestrictedWheelBound) {
    let lastWheelZoomTime = 0;
    const handleWheel = (event) => {
      if (!event.deltaY) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const now = Date.now();
      if (now - lastWheelZoomTime < SKYSAFARI_ZOOM_REPEAT_MS) return;

      lastWheelZoomTime = now;
      Promise.resolve(
        _zoomSkySafariWidgetSettings(
          ssww,
          event.deltaY > 0 ? 1.18 : 0.84,
        ),
      ).catch((error) =>
        console.warn("SkySafari unrestricted wheel zoom failed", error),
      );
    };

    rootElement.addEventListener("wheel", handleWheel, {
      capture: true,
      passive: false,
    });
    rootElement._portalSkyCastUnrestrictedWheelBound = true;
    rootElement._portalSkyCastUnrestrictedWheelCleanup = () => {
      rootElement.removeEventListener("wheel", handleWheel, true);
      delete rootElement._portalSkyCastUnrestrictedWheelBound;
      delete rootElement._portalSkyCastUnrestrictedWheelCleanup;
    };
  }

  ssww._portalSkyCastUnrestrictedZoomCleanup = () => {
    ssww.element?.[0]
      ?.querySelectorAll("#zoomInButton, #zoomOutButton")
      .forEach((control) =>
        control._portalSkyCastUnrestrictedZoomCleanup?.(),
      );
    ssww.element?.[0]?._portalSkyCastUnrestrictedWheelCleanup?.();
  };

  if ((!hasZoomIn || !hasZoomOut) && attempt < 20) {
    clearTimeout(ssww._portalSkyCastUnrestrictedZoomInstallTimer);
    ssww._portalSkyCastUnrestrictedZoomInstallTimer = setTimeout(
      () =>
        _attachSkyCastUnrestrictedZoomControls(containerId, ssww, attempt + 1),
      150,
    );
  }
};

const SkySafariWebWidget = {
  storeLocationInCookie: (loc) => {
    Cookies.createCookie("homeLocation-longitude", loc.longitude, 14);
    Cookies.createCookie("homeLocation-latitude", loc.latitude, 14);
    Cookies.createCookie("homeLocation-elevation", loc.elevation, 14);
    Cookies.createCookie("homeLocation-timezone", loc.timezone, 14);
  },

  getLocationFromCookie: () => {
    const longitude = parseFloat(Cookies.readCookie("homeLocation-longitude"));
    const latitude = parseFloat(Cookies.readCookie("homeLocation-latitude"));
    let elevation;
    let timezone;

    if (Number.isFinite(longitude) && Number.isFinite(latitude)) {
      elevation = parseFloat(Cookies.readCookie("homeLocation-elevation"));
      timezone = parseFloat(Cookies.readCookie("homeLocation-timezone"));

      return {
        longitude: longitude,
        latitude: latitude,
        elevation: elevation,
        timezone: timezone,
      };
    } else {
      return null;
    }
  },

  loadCSS: () => {
    if (cssLoaded) return;
    cssLoaded = true;

    const linkElement = document.createElement("link");
    linkElement.rel = "stylesheet";
    linkElement.href = config.widget.cssUrl;
    document.head.appendChild(linkElement);
  },

  loadScript: () => {
    return new Promise((resolve, reject) => {
      if (window.skysafari) {
        resolve();
        return;
      }

      const scripts = Array.from(document.getElementsByTagName("script"));
      const scriptAlreadyLoaded = scripts.some(
        (script) => script.src === config.widget.scriptUrl,
      );

      if (scriptAlreadyLoaded) {
        // Wait for script to load, timeout after ~15s
        let attempts = 0;
        const checkInterval = setInterval(() => {
          if (window.skysafari) {
            clearInterval(checkInterval);
            resolve();
          } else if (++attempts >= 150) {
            clearInterval(checkInterval);
            reject(new Error("SkySafari widget script failed to initialize after 15s"));
          }
        }, 100);
        return;
      }

      const scriptElement = document.createElement("script");
      scriptElement.src = config.widget.scriptUrl;
      scriptElement.onload = resolve;
      scriptElement.onerror = reject;
      document.head.appendChild(scriptElement);
    });
  },

  listenForLocationChange: (newLoc) => {
    SkySafariWebWidget.storeLocationInCookie(newLoc);
  },

  getExtraLocations: () => {
    var extraLocations = [];
    if (Portal.Current.Collections.observingSites) {
      _.each(Portal.Current.Collections.observingSites, (observingSite) => {
        const location = observingSite.get("location");
        if (!location) return;
        extraLocations.push({
          latitude: parseFloat(location._latitude),
          longitude: parseFloat(location._longitude),
          name: observingSite.get("name") || "",
          elevation: observingSite.get("elevation"),
          timezone: parseFloat(observingSite.get("timezone")),
        });
      });
      extraLocations.sort((a, b) => {
        var nameA = a.name.toUpperCase(); // ignore upper and lowercase
        var nameB = b.name.toUpperCase();

        var nameAIsCurrent = nameA.indexOf("CURRENT LOCATION") === 0;
        var nameBIsCurrent = nameB.indexOf("CURRENT LOCATION") === 0;

        if (nameAIsCurrent !== nameBIsCurrent) {
          return nameBIsCurrent ? -1 : 1;
        }

        if (nameA < nameB) {
          return -1;
        }

        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
      extraLocations = _.uniq(extraLocations, (loc, key, a) => loc.name);
    }

    return extraLocations;
  },

  getExtraContextMenuOptions: () => {
    var extraContextMenuOptions = [];
    // Create a new observation of the target object
    extraContextMenuOptions.push({
      context: Portal.Current.Collections.skyObjectObservations,
      label: "New Observation...",
      callback: (data) => {
        var target = JSON.stringify(data.contextMenuTargetObject);
        var encodedTarget = encodeURIComponent(target);
        Backbone.history.navigate(
          "#/sky-object-observation/add/" + encodedTarget,
          {
            trigger: false,
          },
        );
      },
    });

    // Add the target object to an observing list
    var observingListsMenuOptions = [];

    var observingLists = Portal.Current.Collections.observingLists;
    observingLists.forEach((observingList) => {
      observingListsMenuOptions.push({
        context: observingList,
        label: observingList.get("title"),
        callback: (data) => {
          var target = JSON.stringify(data.contextMenuTargetObject);
          var encodedTarget = encodeURIComponent(target);
          Backbone.history.navigate(
            "#/observing-list/edit/" + observingList.id + "/" + encodedTarget,
            { trigger: false },
          );
        },
      });
    });
    if (observingListsMenuOptions.length > 0) {
      extraContextMenuOptions.push({
        label: "Add To Observing List",
        extraContextMenuOptions: observingListsMenuOptions,
      });
    }

    return extraContextMenuOptions;
  },

  getAssociated: (containerId) =>
    Portal.Current.skySafariObjects
      ? Portal.Current.skySafariObjects[containerId]
      : null,

  associate: async function (containerId, model, hasUI) {
    this.loadCSS();
    await this.loadScript();
    hasUI = _.isUndefined(hasUI) ? true : hasUI;
    _lastAssociateParams[containerId] = { model, hasUI };

    Portal.Current.skySafariObjects = Portal.Current.skySafariObjects || {};
    Portal.Current.skySafariObjectInitPromises =
      Portal.Current.skySafariObjectInitPromises || {};
    Portal.Current.skySafariObjectInitStates =
      Portal.Current.skySafariObjectInitStates || {};
    Portal.Current.skySafariObjectOperationChains =
      Portal.Current.skySafariObjectOperationChains || {};

    let ssww = Portal.Current.skySafariObjects[containerId];

    // Detect dead widget (e.g. WebGL context lost after tab backgrounded)
    if (
      Portal.Current.skySafariObjects[containerId] &&
      !_isWidgetHealthy(containerId)
    ) {
      if (_shouldRecoverContextLoss(containerId)) {
        console.warn(
          `SkySafari widget "${containerId}" context lost, recreating`,
        );
      }
      const deadEl =
        Portal.Current.skySafariObjects[containerId].element?.[0];
      if (deadEl?.parentElement) {
        const fresh = document.createElement("div");
        fresh.id = containerId;
        deadEl.replaceWith(fresh);
      }
      this.disassociate(containerId, { forceDestroy: true });
      ssww = Portal.Current.skySafariObjects[containerId];
    }

    const initPromises = Portal.Current.skySafariObjectInitPromises;
    const initStates = Portal.Current.skySafariObjectInitStates;
    const operationChains = Portal.Current.skySafariObjectOperationChains;

    if (!ssww) {
      let resolveInit;
      let rejectInit;
      initPromises[containerId] = new Promise((resolve, reject) => {
        resolveInit = resolve;
        rejectInit = reject;
      });
      initStates[containerId] = "pending";

      let newSSWW;
      // Initial widget options
      const sswwOptions = {
        apiKey: config.skysafari.key,
        loadingScreen: { text: "Loading SkySafari Web for LiveSky..." },
        errorScreen: true,
        creationSuccessCallback: () => {
          newSSWW.element = $("#" + containerId);
          _attachContextLossListener(containerId, newSSWW);
          SkySafariWebWidget.updateFOVIndicators(newSSWW);
          const loc = SkySafariWebWidget.getLocationFromCookie();

          if (loc) {
            newSSWW.location.setHome(loc);
          }

          newSSWW.location.listenForChange(
            "homeLocation",
            this.listenForLocationChange,
          );

          // Start trap just before returning to the widget code that will
          // bind the broken scroll handler right after this callback returns.
          let scrollTrap;
          try {
            scrollTrap = _startScrollTrap();
          } catch (error) {
            initStates[containerId] = "failed";
            delete Portal.Current.skySafariObjects[containerId];
            rejectInit(error);
            throw error;
          }

          // Defer cleanup so it runs after the widget adds its broken scroll
          // handler (widget binds it right after calling this callback).
          setTimeout(() => {
            _removeCapturedScrollHandlers(scrollTrap);
            if (Portal.Current.skySafariObjects?.[containerId] !== newSSWW) {
              return;
            }

            // Add our own correct scroll handler for visibility toggling
            newSSWW._portalScrollHandler = () => {
              if (typeof newSSWW.onScroll === "function") {
                newSSWW.onScroll();
              }
            };
            window.addEventListener("scroll", newSSWW._portalScrollHandler);
          }, 0);
          initStates[containerId] = "ready";
          resolveInit(newSSWW);
        },
        creationFailureCallback: (reason) => {
          newSSWW.element = $("#" + containerId);
          delete Portal.Current.skySafariObjects[containerId];
          delete initPromises[containerId];
          initStates[containerId] = "failed";
          rejectInit(reason);
          console.warn(
            "SkySafariWebWidget failed to create skysafari object:" + reason,
          );
        },
        toolbar: hasUI ? { hasAllOptions: true, showFOVSwitch: true } : false,
        statusbar: hasUI,
        scenecontrols: hasUI ? { hasAllOptions: true } : false,
        mediaviewer: hasUI ? { availableForInfo: true } : false,
        skyChart: {
          skydata: {
            large: true,
            streamingTychoStars: true,
            newDataLoadedCallback: (inDatabaseID) => {
              // If you want to be notified after a new, probably larger DB is downloaded and
              // loaded, you can get that here
              // console.log('Loaded new datafile ' + inDatabaseID)
            },
          },
        },
      };
      // Additional options for premium users
      if (Portal?.Current?.access?.get("hasPremium") && hasUI) {
        // If they are a premium user, add items to the context popup and extra locations
        sswwOptions.skyChart.extraContextMenuOptions =
          SkySafariWebWidget.getExtraContextMenuOptions();
        sswwOptions.skyChart.extraLocations = SkySafariWebWidget.getExtraLocations();
      }

      newSSWW = window.skysafari(containerId, sswwOptions);
      Portal.Current.skySafariObjects[containerId] = newSSWW;
    }

    const initPromise =
      initPromises[containerId] ||
      Promise.resolve(Portal.Current.skySafariObjects[containerId]);

    const operation = () =>
      initPromise.then((associatedSSWW) => {
        // Ignore stale async work from a previous route/view lifecycle.
        if (Portal.Current.skySafariObjects?.[containerId] !== associatedSSWW) {
          return associatedSSWW;
        }

        const previousHasUI = associatedSSWW.hasUI;
        associatedSSWW.hasUI = hasUI;
        const shouldUpdateOptions =
          associatedSSWW._optionsInitialized !== true ||
          previousHasUI !== hasUI;

        // Update options
        const newOptions = {
          toolbar: hasUI ? { hasAllOptions: true, showFOVSwitch: true } : false,
          statusbar: hasUI,
          scenecontrols: hasUI ? { hasAllOptions: true } : false,
          mediaviewer: hasUI ? { availableForInfo: true } : false,
        };
        if (shouldUpdateOptions && typeof associatedSSWW.setOptions === "function") {
          associatedSSWW.setOptions(newOptions);
          associatedSSWW._optionsInitialized = true;
        }

        // Update premium data
        if (Portal.Current.access.get("hasPremium") && hasUI) {
          SkySafariWebWidget.updateObservingLists(associatedSSWW);
          SkySafariWebWidget.updateExtraLocations(associatedSSWW);
          SkySafariWebWidget.updateFOVIndicators(associatedSSWW);
        }

        const containerElement = $("#" + containerId);
        const hasContainer = containerElement.length > 0;
        const hasWidgetElement = associatedSSWW.element?.length > 0;
        const sameElement =
          hasContainer &&
          hasWidgetElement &&
          containerElement.get(0) === associatedSSWW.element.get(0);

        if (hasContainer && hasWidgetElement && !sameElement) {
          containerElement.replaceWith(associatedSSWW.element);
        }

        if (hasUI) {
          _attachSkyCastUnrestrictedZoomControls(containerId, associatedSSWW);
        }

        if (!model) {
          return associatedSSWW;
        }

        return Promise.resolve(model.displayInSkySafariWidget(associatedSSWW)).then(
          () => associatedSSWW,
        );
      });

    const previousChain = operationChains[containerId] || Promise.resolve();
    const chainedOperation = previousChain.catch(() => {}).then(operation);
    operationChains[containerId] = chainedOperation.catch(() => {});

    return chainedOperation.catch((reason) => {
      console.error(reason);
      throw reason;
    });
  },

  disassociate: (containerId, options = {}) => {
    if (!Portal.Current.skySafariObjects?.[containerId]) return;
    const { forceDestroy = false } = options;
    const ssww = Portal.Current.skySafariObjects[containerId];
    const initState = Portal.Current.skySafariObjectInitStates?.[containerId];

    // Keep shared widget instances warm across route changes.
    if (
      PERSISTENT_CONTAINER_IDS.has(containerId) &&
      !forceDestroy &&
      initState === "ready"
    ) {
      const parkingContainer = ensureWidgetParkingContainer();
      if (parkingContainer && ssww?.element?.length) {
        $(parkingContainer).append(ssww.element);
      }
      return;
    }

    // Remove our scroll handler
    if (ssww._portalScrollHandler) {
      window.removeEventListener("scroll", ssww._portalScrollHandler);
    }

    if (ssww._portalSkyCastUnrestrictedZoomCleanup) {
      ssww._portalSkyCastUnrestrictedZoomCleanup();
    }
    clearTimeout(ssww._portalSkyCastUnrestrictedZoomInstallTimer);

    // Remove context loss listener
    if (ssww._contextLostHandler && ssww._contextLostCanvas) {
      ssww._contextLostCanvas.removeEventListener(
        "webglcontextlost",
        ssww._contextLostHandler,
      );
    }
    delete _contextWasLostFlags[containerId];

    // Call destroy/dispose if the widget API supports it. Guard it: the widget's
    // own destroy() can throw (e.g. skysafari_main.js retireApiValue attempting to
    // delete an index off a DOMTokenList), and an uncaught throw here would both
    // surface an error on navigation and abort the state cleanup below (leaking the
    // instance in Portal.Current.skySafariObjects).
    try {
      if (typeof ssww.destroy === "function") {
        ssww.destroy();
      } else if (typeof ssww.dispose === "function") {
        ssww.dispose();
      }
    } catch (error) {
      console.warn(
        `SkySafari widget "${containerId}" destroy() failed; continuing cleanup`,
        error,
      );
    }

    if (Portal.Current.skySafariObjectInitPromises) {
      delete Portal.Current.skySafariObjectInitPromises[containerId];
    }
    if (Portal.Current.skySafariObjectInitStates) {
      delete Portal.Current.skySafariObjectInitStates[containerId];
    }
    if (Portal.Current.skySafariObjectOperationChains) {
      delete Portal.Current.skySafariObjectOperationChains[containerId];
    }
    delete Portal.Current.skySafariObjects[containerId];
  },

  getSnapShot: function (containerId, model, dontReset) {
    return new Promise((resolve, reject) => {
      const ssww = Portal.Current.skySafariObjects?.[containerId];

      if (ssww && dontReset) {
        const snapshot = ssww.snapShotBase64();
        resolve(snapshot);
      } else {
        this.associate(containerId, model, false)
          .then((ssww) => resolve(ssww.snapShotBase64()))
          .catch(reject);
      }
    });
  },

  updateObservingLists: (ssww) => {
    if (!ssww?.hasUI) {
      return;
    }

    // Update the context menu to reflect the updated observing lists
    ssww.extraContextMenuOptions.removeAll();

    const extraMenuOptions = SkySafariWebWidget.getExtraContextMenuOptions();
    extraMenuOptions.forEach((menuOption) => {
      ssww.extraContextMenuOptions.add(menuOption);
    });
  },

  updateExtraLocations: (ssww) => {
    if (!ssww?.hasUI) {
      return;
    }

    // Update the context menu to reflect the updated locations
    ssww.extraLocations.removeAll();

    const extraLocations = SkySafariWebWidget.getExtraLocations();
    extraLocations.forEach((location) => {
      ssww.extraLocations.add(location);
    });
  },

  updateFOVIndicators: (ssww) => {
    if (!ssww?.hasUI) {
      return;
    }

    // Start with a clean slate
    ssww.fovIndicators.removeAll();

    // This is a constant defined in the SkySafari widget, which likely
    // isn't loaded yet, bit of a chicken and egg thing...
    const TEL_MAX_FIELDS = 5;

    const fovIndicators = Portal.Current.Collections?.fovIndicators;
    if (!fovIndicators) {
      return;
    }

    const convertToRadians = (degrees) => (degrees * Math.PI) / 180;

    const fovObjects = fovIndicators
      .filter((indicator) => indicator.get("fovOn") === true)
      .slice(0, TEL_MAX_FIELDS)
      .map((fovIndicator) => {
        const fov = fovIndicator.getFOV();
        return {
          width: convertToRadians(fov.width), // field width, radians. If zero, field is hidden.
          height: convertToRadians(fov.height), // field height, radians. If zero, field is circular, with diameter == width. If nonzero, field is rectangular.
          offsetX: fov.offsetX, // horizontal offset from telescope optical axis, radians.
          offsetY: fov.offsetY, // vertical offset from telescope optical axis, radians.
          name: fovIndicator.getDisplayName(), // optional display name for this field of view.
        };
      });

    fovObjects.forEach((fovIndicator, index) => {
      ssww.fovIndicators.add(index, fovIndicator);
    });
  },
};

// Recover a single persistent widget whose WebGL context is dead.
const _recoverWidget = (containerId) => {
  if (!Portal.Current?.skySafariObjects?.[containerId]) return;
  if (_isWidgetHealthy(containerId)) return;
  if (!_shouldRecoverContextLoss(containerId)) return;

  console.warn(`SkySafari widget "${containerId}" context lost, recovering`);

  const deadEl =
    Portal.Current.skySafariObjects[containerId].element?.[0];
  const parking = document.getElementById(WIDGET_PARKING_ID);
  const isInPage =
    deadEl?.parentElement && deadEl.parentElement !== parking;

  if (isInPage) {
    const fresh = document.createElement("div");
    fresh.id = containerId;
    deadEl.replaceWith(fresh);
  }

  SkySafariWebWidget.disassociate(containerId, { forceDestroy: true });

  if (isInPage) {
    const params = _lastAssociateParams[containerId];
    if (params) {
      SkySafariWebWidget.associate(containerId, params.model, params.hasUI);
    }
  }
};

// Recover persistent widgets when tab becomes visible after WebGL context loss
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;

    for (const containerId of PERSISTENT_CONTAINER_IDS) {
      _recoverWidget(containerId);
    }

    // Delayed re-check: catches edge cases where webglcontextlost event
    // was missed or context dies shortly after wake
    setTimeout(() => {
      if (document.visibilityState !== "visible") return;
      for (const containerId of PERSISTENT_CONTAINER_IDS) {
        _recoverWidget(containerId);
      }
    }, 2000);
  });
}

export default SkySafariWebWidget;
